'use client';

import {
  Box,
  Button,
  SystemIcon,
  IconButton,
  Typography,
  RangeSlider,
  LoadingIndicator,
} from '@wishket/design-system';
import { twMerge } from 'tailwind-merge';
import Cropper, { Area } from 'react-easy-crop';
import { ChangeEvent, FormEvent, useEffect } from 'react';

import { BottomInfo } from './CollectionThumbnail/BottomInfo';
import CollectionModalContainer from './CollectionModalContainer';
import { MOCKED_AI_THUMBNAIL_PROMPT } from './Collection.constants';

import { PATH } from '@/shared/constants';
import { Icon, IconName } from '@/shared/Components';
import { getScrollbarStyle, scrollToElementById } from '@/shared/utils';

const DEFAULT_THUMBNAIL_IMAGE_URL = `${PATH.MEDIA_YOZM_IMAGE}/default_collection_thumbnail.svg`;

interface CollectionThumbnailFormProps {
  thumbnail: {
    crop: { x: number; y: number };
    zoom: number;
    imageSrc: string;
  };
  onThumbnailChange: {
    zoom: (zoom: number) => void;
    crop: (crop: { x: number; y: number }) => void;
    cropComplete: (croppedAreaPixels: Area) => void;
  };
  slider: {
    min: number;
    max: number;
  };
  onSliderChange: {
    set: (value: number) => void;
    plus: () => void;
    minus: () => void;
  };
  prompt: string;
  onClose: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  aiSelected: () => void;
  resetPrompt: () => void;
  promptSubmit: (prompt: string) => void;
  onPromptChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  isPromptVisible: boolean;
  isPromptLoading: boolean;
  deleteThumbnail: () => void;
  openImageSelector: () => void;
  handlePromptClick: (prompt: string) => void;
}

const CollectionThumbnailForm = ({
  prompt,
  slider,
  onClose,
  onSubmit,
  thumbnail,
  aiSelected,
  resetPrompt,
  promptSubmit,
  onPromptChange,
  onSliderChange,
  deleteThumbnail,
  isPromptLoading,
  isPromptVisible,
  onThumbnailChange,
  openImageSelector,
  handlePromptClick,
}: CollectionThumbnailFormProps) => {
  const cropperStyle = {
    cropAreaStyle: {
      border: 'none',
      color: 'transparent',
    },
    containerStyle: { cursor: 'default' },
  };
  const isDefaultImage = thumbnail.imageSrc === DEFAULT_THUMBNAIL_IMAGE_URL;

  useEffect(() => {
    if (isPromptVisible) {
      scrollToElementById('collection_thumbnail_modal_textarea');
    }
  }, [isPromptVisible]);

  return (
    <CollectionModalContainer
      title="컬렉션 표지 등록"
      submitButtonText="등록하기"
      bottomStickyElement={<BottomInfo />}
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <Box
        className={twMerge(
          'flex min-h-[calc(100%_-_84px)] w-full flex-col gap-8 overflow-y-scroll pb-10 pt-6 desktop:pb-8',
          getScrollbarStyle()
        )}
      >
        <Box className="w-full overflow-hidden rounded-2xl border border-w-gray-200">
          <Box className="relative aspect-video w-full">
            <Cropper
              crop={thumbnail.crop}
              zoom={thumbnail.zoom}
              showGrid={false}
              style={cropperStyle}
              onTouchRequest={undefined}
              onWheelRequest={undefined}
              image={thumbnail.imageSrc}
              aspect={16 / 9}
              objectFit="cover"
              onCropChange={onThumbnailChange.crop}
              onZoomChange={onThumbnailChange.zoom}
              onCropComplete={(_: Area, croppedAreaPixels: Area) =>
                onThumbnailChange.cropComplete(croppedAreaPixels)
              }
            />
            {!isDefaultImage && (
              <IconButton
                type="button"
                onClick={deleteThumbnail}
                className="absolute bottom-3 right-3 rounded-full bg-black/50 backdrop-blur-[2px]"
              >
                <SystemIcon name="medium_trash" className="text-white" />
              </IconButton>
            )}
          </Box>
          <Box className="flex items-center justify-center border-t py-5">
            <Box className="flex items-center gap-4">
              <IconButton
                onClick={() => onSliderChange.minus()}
                type="button"
                size="sm"
              >
                <SystemIcon
                  name="medium_glasses_minus"
                  className="text-w-gray-600"
                />
              </IconButton>
              <RangeSlider
                min={1}
                max={2}
                step={0.1}
                className="w-[156px]"
                value={slider}
                disabled={isDefaultImage}
                onChange={({ max }) => onSliderChange.set(max)}
              />
              <IconButton
                onClick={() => onSliderChange.plus()}
                type="button"
                size="sm"
              >
                <SystemIcon
                  name="medium_glasses_plus"
                  className="text-w-gray-600"
                />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Box className="flex w-full gap-4 ">
          <Button
            variant="outlined"
            className="flex-1"
            type="button"
            onClick={aiSelected}
          >
            <SystemIcon name="medium_magic" className="text-w-gray-600" />
            <Typography variant="body1" className="text-w-gray-600">
              AI 만들기
            </Typography>
          </Button>
          <Button
            variant="outlined"
            className="flex-1"
            type="button"
            onClick={openImageSelector}
          >
            <SystemIcon name="medium_upload" className="text-w-gray-600" />
            <Typography variant="body1" className="text-w-gray-600">
              사진 올리기
            </Typography>
          </Button>
        </Box>
        {isPromptVisible && (
          <>
            <Box className="group flex h-fit w-full flex-col gap-3 overflow-hidden rounded-xl border py-2.5 focus-within:border-primary">
              <textarea
                rows={2}
                value={prompt}
                id="collection_thumbnail_modal_textarea"
                placeholder="예) 업무를 하고 있는 고양이를 그려줘"
                className="h-[100px] w-full resize-none pl-4 text-w-gray-600 placeholder-w-gray-300 outline-none scrollbar-list typo-body1"
                onChange={onPromptChange}
              />
              <Box className="flex w-full shrink-0 items-center justify-end gap-3 px-4">
                {prompt && !isPromptLoading && (
                  <IconButton onClick={resetPrompt} type="button" size="sm">
                    <SystemIcon
                      solid
                      name="large_reset"
                      className="text-w-gray-600"
                    />
                  </IconButton>
                )}
                {isPromptLoading ? (
                  <Box className="relative size-6">
                    <LoadingIndicator size="sm" className="left-0" />
                  </Box>
                ) : (
                  <IconButton
                    type="button"
                    size="sm"
                    onClick={() => promptSubmit(prompt)}
                  >
                    <SystemIcon
                      solid
                      name="large_send"
                      className="text-primary"
                    />
                  </IconButton>
                )}
              </Box>
            </Box>
            <Box className="flex flex-wrap gap-3">
              {MOCKED_AI_THUMBNAIL_PROMPT.map(({ name, text, icon }) => (
                <Box
                  key={name}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePromptClick(text);
                  }}
                  className="flex cursor-pointer items-center justify-center gap-2 rounded-3xl bg-w-gray-50 p-1 pr-4 ring-1 ring-transparent hover:ring-primary"
                >
                  <Icon name={icon as IconName} />
                  <Typography variant="body1" className="text-w-gray-900">
                    {name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </CollectionModalContainer>
  );
};

export default CollectionThumbnailForm;
